import React, { useMemo, useRef, useEffect } from 'react';
import { View, StyleSheet, Animated } from 'react-native';
import Svg, { Circle, Line, G } from 'react-native-svg';
import colors from '../../../../config/colors';

interface AnalogClockProps {
	size?: number;
	isRunning?: boolean;
}

const RING_STROKE_WIDTH = 6;
const HOUR_TICK_LENGTH = 10;
const MINUTE_TICK_LENGTH = 5;
const TICK_OFFSET = 4; // gap between ring inner edge and tick end
const OUTER_RING_STROKE = 6; // thickness of the outer ring
const OUTER_RING_GAP = 0; // gap between main ring and outer ring

const AnalogClock = ({ size = 200, isRunning = false }: AnalogClockProps) => {
	const center = size / 2;
	const ringRadius = center - RING_STROKE_WIDTH / 2 - 2;
	const ringColor = isRunning ? '#4CAF50' : colors.primary;
	const bgRingColor = colors.white;
	const outerRingColor = isRunning ? colors.greenLightClock : '#E2EDF4';
	const outerRingSize = size + (OUTER_RING_GAP + OUTER_RING_STROKE) * 2;

	const breathAnim = useRef(new Animated.Value(1)).current;
	useEffect(() => {
		if (isRunning) {
			const anim = Animated.loop(
				Animated.sequence([
					Animated.timing(breathAnim, {
						toValue: 0.3,
						duration: 1200,
						useNativeDriver: true,
					}),
					Animated.timing(breathAnim, {
						toValue: 1,
						duration: 1200,
						useNativeDriver: true,
					}),
				]),
			);
			anim.start();
			return () => anim.stop();
		} else {
			breathAnim.stopAnimation();
			breathAnim.setValue(1);
		}
	}, [isRunning]);

	const ticks = useMemo(() => {
		const items: {
			x1: number;
			y1: number;
			x2: number;
			y2: number;
			isHour: boolean;
		}[] = [];

		for (let i = 0; i < 60; i++) {
			const angle = (i * 6 - 90) * (Math.PI / 180);
			const isHour = i % 5 === 0;
			const tickLen = isHour ? HOUR_TICK_LENGTH : MINUTE_TICK_LENGTH;
			const outerR = ringRadius - RING_STROKE_WIDTH / 2 - TICK_OFFSET;
			const innerR = outerR - tickLen;

			items.push({
				x1: center + innerR * Math.cos(angle),
				y1: center + innerR * Math.sin(angle),
				x2: center + outerR * Math.cos(angle),
				y2: center + outerR * Math.sin(angle),
				isHour,
			});
		}
		return items;
	}, [size]);

	return (
		<View style={[styles.container, { width: outerRingSize, height: outerRingSize }]}>
			{/* Outer background ring */}
			<Animated.View
				style={[
					styles.outerRing,
					{
						width: outerRingSize,
						height: outerRingSize,
						borderRadius: outerRingSize / 2,
						borderWidth: OUTER_RING_STROKE,
						borderColor: outerRingColor,
						opacity: isRunning ? breathAnim : 1,
					},
				]}
			/>
			<Svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
				{/* Background fill circle */}
				<Circle
					cx={center}
					cy={center}
					r={ringRadius}
					fill={bgRingColor}
					stroke='none'
				/>
				{/* Main ring */}
				<Circle
					cx={center}
					cy={center}
					r={ringRadius}
					fill='none'
					stroke={ringColor}
					strokeWidth={RING_STROKE_WIDTH}
					strokeLinecap='round'
				/>
				{/* Tick marks */}
				<G>
					{ticks.map((tick, idx) => (
						<Line
							key={idx}
							x1={tick.x1}
							y1={tick.y1}
							x2={tick.x2}
							y2={tick.y2}
							stroke={tick.isHour ? '#B0BEC5' : '#CFD8DC'}
							strokeWidth={tick.isHour ? 1.5 : 0.8}
							strokeLinecap='round'
						/>
					))}
				</G>
			</Svg>
		</View>
	);
};

const styles = StyleSheet.create({
	container: {
		alignItems: 'center',
		justifyContent: 'center',
	},
	outerRing: {
		position: 'absolute',
	},
});

export default AnalogClock;
